import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import AuthShell from "../components/auth/AuthShell";
import PasswordChecklist from "../components/auth/PasswordChecklist";
import { AppAlert, AppButton, AppField } from "../components/ui/AppField";
import { isSignupPasswordValid } from "../utils/passwordRules";
import {
  clearAppAuthError,
  performAppPasswordReset,
  selectAppAuthError,
  selectAppAuthLoading,
} from "../redux/appAuthSlice";

const ResetPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const loading = useSelector(selectAppAuthLoading);
  const error = useSelector(selectAppAuthError);

  const contactMethod = location.state?.contactMethod || "email";
  const accountEmail = location.state?.email || "";
  const phoneNumber = location.state?.phoneNumber || "";
  const recoveryEmail = location.state?.recoveryEmail || "";
  const codeDestination = contactMethod === "phone" ? recoveryEmail : accountEmail;

  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    if (!codeDestination) {
      navigate("/app/forgot-password", { replace: true });
    }
  }, [codeDestination, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLocalError("");

    const sanitizedCode = resetCode.trim();
    const sanitizedPassword = newPassword.trim();

    if (sanitizedCode.length !== 6) {
      setLocalError("Enter the 6-digit reset code.");
      return;
    }

    if (!isSignupPasswordValid(sanitizedPassword)) {
      return;
    }

    dispatch(clearAppAuthError());

    try {
      const payload = {
        resetCode: sanitizedCode,
        newPassword: sanitizedPassword,
      };

      if (contactMethod === "phone") {
        payload.phoneNumber = phoneNumber;
        payload.recoveryEmail = recoveryEmail;
      } else {
        payload.email = accountEmail;
      }

      await dispatch(performAppPasswordReset(payload)).unwrap();
      navigate("/app/login", {
        replace: true,
        state: { message: "Password reset successful. Log in with your new password." },
      });
    } catch {
      /* slice sets error state */
    }
  };

  const displayError = localError || error;

  const passwordToggle = (
    <button
      type="button"
      onClick={() => setShowPassword((v) => !v)}
      className="mr-3 p-1 text-white/40 hover:text-white transition-colors"
      aria-label={showPassword ? "Hide password" : "Show password"}
    >
      {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
    </button>
  );

  return (
    <AuthShell
      title="Reset password"
      subtitle={
        codeDestination ? (
          <>
            Enter the code sent to{" "}
            <span className="font-semibold text-white/85">{codeDestination}</span> and choose a
            new password.
          </>
        ) : (
          "Enter your reset code and new password."
        )
      }
      footer={
        <Link
          to="/app/forgot-password"
          className="app-btn-ghost flex h-12 w-full items-center justify-center rounded-lg text-[14px] font-extrabold tracking-tight text-white"
        >
          Request a new code
        </Link>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {displayError ? <AppAlert>{displayError}</AppAlert> : null}

        <AppField
          id="reset-code"
          label="Reset code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={resetCode}
          onChange={(e) => setResetCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          className="[&_input]:text-center [&_input]:text-lg [&_input]:tracking-[0.35em] [&_input]:font-semibold"
        />

        <AppField
          id="reset-password"
          label="New password"
          type={showPassword ? "text" : "password"}
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          suffix={passwordToggle}
        />
        <PasswordChecklist password={newPassword} />

        <AppButton type="submit" disabled={loading}>
          {loading ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 size={16} className="animate-spin" />
              Resetting password
            </span>
          ) : (
            "Reset password"
          )}
        </AppButton>
      </form>
    </AuthShell>
  );
};

export default ResetPassword;
